import {
  ENTITY_CLASS,
  type FacilityKey,
  type FacilityQuery,
  type OrganizationKey,
  type UserKey,
  isFacilityKey,
} from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";

import { fetchData, findFacilities } from "./functions";
import { QueryKey } from "./query";
import { NETWORK_SEMAPHORE } from "./semaphore";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

// =============================================================
// 施設の単体取得
// =============================================================

/**
 * 施設を取得する。存在しない場合、`null`を返す。
 * * ユーザー名が施設キーになっている場合の取得に使用し易いように、
 * 組織キーやユーザーキーの指定出来る。その場合、`undefined`を返す。
 * @param facilityKey 施設キー
 * @returns 施設
 */
export async function findFacility(
  facilityKey: FacilityKey | OrganizationKey | UserKey | false | null | undefined,
) {
  if (!isFacilityKey(facilityKey)) {
    return undefined;
  }
  const { output } = await fetchData({ type: "Entity::Facility", input: { facilityKey } });
  if (output instanceof Error) {
    throw output;
  } else {
    return output;
  }
}

function FacilityQueryKey(
  facilityKey: FacilityKey | OrganizationKey | UserKey | false | null | undefined,
) {
  return QueryKey(ENTITY_CLASS.FACILITY, facilityKey);
}

export function updateFacilityQueryData(
  facilityKey: FacilityKey | false | null | undefined,
  newFacility: ReturnType<typeof useFacilityQuery>["data"] | false,
) {
  if (facilityKey && newFacility !== false && newFacility !== undefined) {
    TANSTACK_QUERY_CLIENT.setQueryData(
      FacilityQueryKey(facilityKey),
      function (oldResident: ReturnType<typeof useFacilityQuery>["data"]) {
        return !oldResident ||
          !newFacility ||
          (Temporal.PlainDateTime.compare(oldResident.createdAt, newFacility.createdAt) ||
            Temporal.PlainDateTime.compare(oldResident.updatedAt, newFacility.updatedAt)) < 0
          ? newFacility
          : oldResident;
      },
    );
  }
}

export function useFacilityQuery(
  facilityKey: FacilityKey | OrganizationKey | UserKey | false | null | undefined,
) {
  return useQuery({
    queryKey: FacilityQueryKey(facilityKey),
    async queryFn() {
      return findFacility(facilityKey);
    },

    enabled: isFacilityKey(facilityKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

// =============================================================
// 施設の一覧
// =============================================================

function FacilityInfiniteQueryKey(...parameters: Parameters<typeof useFacilityInfiniteQuery>) {
  return QueryKey(ENTITY_CLASS.FACILITY + "List", ...parameters);
}

export function resetFacilityInfiniteQueries() {
  TANSTACK_QUERY_CLIENT.resetQueries({ queryKey: QueryKey(ENTITY_CLASS.FACILITY + "List") });
}

export function useFacilityInfiniteQuery(
  query: FacilityQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  return useInfiniteQuery({
    queryKey: FacilityInfiniteQueryKey(query, options),
    async queryFn(context) {
      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        const { facilities, numberOfErrors, nextPageToken } = await findFacilities({
          ...query,
          ...context.pageParam,
        });

        for (const facility of facilities) {
          updateFacilityQueryData(facility.facilityKey, facility);
        }

        return { facilities, numberOfErrors, nextPageToken };
      } finally {
        release();
      }
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}
